"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const input_dialog_1 = require("../input-dialog");
const DecimalPoint = require("./lib/decimal-point");
require("./number-dialog.scss");
const InputButtonIdentifiers = ["backspace", "plus-minus", "decimal-point"];
const DigitButtons = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]
    .map(n => ({ identifier: `number-${n}`, value: n }));
const NumberButtonIdentifiers = DigitButtons.map(button => button.identifier);
const ZeroPattern = new RegExp("^0+$"); // zeroes only
const IntegerPattern = new RegExp("^[-0-9]+$"); // minus and digits only
const UnsignedPattern = new RegExp("^[^-]+$"); // no minus
class NumberDialog extends input_dialog_1.default {
    constructor(_numberDialogConfig = {}) {
        super(_numberDialogConfig); // call InputDialog's constructor
        this._numberDialogConfig = _numberDialogConfig;
        // we have the input in the form of a JQuery object
        const input = this.input;
        // keep the original input type to always be able to restore it
        this._originalInputType = input.attr("type");
        // define the focus event callback
        this._focusCallback = (focusEvent) => {
            this.handleInputFocus(focusEvent);
        };
        // define the key event callback
        this._keyCallback = (keyEvent) => {
            this.handleInputKeyEvent(keyEvent);
        };
    }
    get decimalPointCharacters() {
        return this._numberDialogConfig.decimalPointCharacters || DecimalPoint.KnownIncarnations;
    }
    get decimalPointCharacter() {
        return this._numberDialogConfig.decimalPointCharacter || this.canonicalDecimalPointCharacter;
    }
    get canonicalDecimalPointCharacter() {
        return this._numberDialogConfig.canonicalDecimalPointCharacter || ".";
    }
    getCssClass() {
        return this._numberDialogConfig.cssClass || "number-modal-dialog";
    }
    get inputNextToButtonsCssClass() {
        const inputClass = this._numberDialogConfig.inputNextToButtonsCssClass || "next-to-buttons";
        if (this._numberDialogConfig.isCodeLock) {
            return `${inputClass} code-lock`;
        }
        return inputClass;
    }
    get digitButtonsDisplayed() {
        const configDigitButtonsDisplayed = this._numberDialogConfig.digitButtonsDisplayed;
        if (typeof configDigitButtonsDisplayed !== "undefined") {
            return configDigitButtonsDisplayed;
        }
        return true; // default
    }
    get hasFooter() {
        return !this.digitButtonsDisplayed;
    }
    get dirtyFlagName() {
        return this._numberDialogConfig.dirtyFlagName || "number-dialog-dirty";
    }
    get bodySelector() {
        const selector = this._numberDialogConfig.bodySelector || ".number-modal-body";
        return `${this.rootSelector} ${selector}`;
    }
    get inputSelector() {
        const selector = this._numberDialogConfig.inputSelector || "input";
        return `${this.bodySelector} ${selector}`;
    }
    get input() {
        return this.jQuery(this.inputSelector);
    }
    get digitButtonSelector() {
        const selector = this._numberDialogConfig.digitButtonSelector || "button";
        return `${this.bodySelector} ${selector}`;
    }
    get decimalPointButtonSelector() {
        const selector = this._numberDialogConfig.decimalPointButtonSelector || ".decimal-point-button";
        return `${this.bodySelector} ${selector}`;
    }
    get plusMinusButtonSelector() {
        const selector = this._numberDialogConfig.plusMinusButtonSelector || ".plus-minus-button";
        return `${this.bodySelector} ${selector}`;
    }
    getButtonMatcher() {
        return clickEvent => {
            const superButtonMatcher = super.getButtonMatcher();
            const superResult = superButtonMatcher(clickEvent);
            if (typeof superResult !== "undefined") {
                return superResult;
            }
            const buttonIdentifiers = []
                .concat(InputButtonIdentifiers)
                .concat(NumberButtonIdentifiers);
            for (let i = 0; i < buttonIdentifiers.length; i++) {
                const identifier = buttonIdentifiers[i];
                if (this.jQuery(clickEvent.target).hasClass(`${identifier}-button`)) {
                    return identifier;
                }
            }
        };
    }
    canonizeDecimalPoint(numberValue, canonicalDecimalPoint) {
        let value = numberValue;
        this.decimalPointCharacters.forEach(decimalPoint => {
            if (decimalPoint !== canonicalDecimalPoint) {
                // make sure all occurrences of a decimal point are "."
                while (value.indexOf(decimalPoint) >= 0) {
                    value = value.replace(decimalPoint, canonicalDecimalPoint);
                }
            }
        });
        return value;
    }
    setInputValue(value, initialize = true) {
        const canonicalValue = this.canonizeDecimalPoint(value, this.decimalPointCharacter);
        // first, try setting the value with this.decimalPointCharacter:
        this.input.val(canonicalValue);
        if (this.input.val() === "") {
            // input does not accept our value => try it with canonization
            const canonicalValue = this.canonizeDecimalPoint(value, ".");
            this.input.val(canonicalValue);
        }
        this.setDirty(!initialize);
        console.debug("INPUT VALUE NOW IS ", this.input.val());
    }
    clearInputValue() {
        this.input.val("");
    }
    getInputValue() {
        const value = this.input.val();
        // not sure if the decimal point ever differs from ".",
        // thus canonicalValue is expected to equal value,
        // but you can't be too careful:
        const canonicalValue = this.canonizeDecimalPoint(value, this.canonicalDecimalPointCharacter);
        return canonicalValue;
    }
    focusInput() {
        this.input.trigger("focus");
    }
    selectInput() {
        this.input.trigger("select");
    }
    setDirty(dirty) {
        this.input.removeClass(this.invalidCssClass);
        if (dirty) {
            this.input.data(this.dirtyFlagName, true);
            this.input.addClass(this.dirtyCssClass);
        }
        else {
            this.input.removeData(this.dirtyFlagName);
            this.input.removeClass(this.dirtyCssClass);
        }
    }
    isDirty() {
        const dirty = this.input.data(this.dirtyFlagName) ? true : false;
        return dirty;
    }
    isValid() {
        const value = this.getInputValue();
        if (!value.length) {
            // not a number => invalid
            return false;
        }
        if (this._numberDialogConfig.integerOnly) {
            if (!IntegerPattern.test(value)) {
                // not an integer => invalid
                return false;
            }
        }
        if (this._numberDialogConfig.unsignedOnly) {
            if (!UnsignedPattern.test(value)) {
                // contains a minus => invalid
                return false;
            }
        }
        const minimum = this._numberDialogConfig.minimum;
        if (typeof minimum !== "undefined") {
            const numberValue = +value; // string-to-number
            if (!isNaN(numberValue)) {
                // value is a normal number
                if (numberValue < minimum) {
                    return false;
                }
            }
        }
        const maximum = this._numberDialogConfig.maximum;
        if (typeof maximum !== "undefined") {
            const numberValue = +value; // string-to-number
            if (!isNaN(numberValue)) {
                // value is a normal number
                if (numberValue > maximum) {
                    return false;
                }
            }
        }
        const tail = value[value.length - 1]; // string of length 1
        const decimalPointsInTail = DecimalPoint.occurringDecimalPoints(tail, this.decimalPointCharacters);
        if (decimalPointsInTail.length) {
            // tail is decimal point => invalid
            return false;
        }
        return true;
    }
    handleButtonClick(buttonIdentifier, resultCallback) {
        if (this.handleInputButtonClick(buttonIdentifier)) {
            // input button clicked
            // make sure that pressing the return key will not lead to another button click event:
            const buttonSelector = `${this.bodySelector} .${buttonIdentifier}-button`;
            this.jQuery(buttonSelector).blur();
        }
        else {
            // dialog button clicked => request dialog closing ("this.hide()") and report a result
            if (buttonIdentifier === this.okCloseButton) {
                if (this.isValid()) {
                    super.handleButtonClick(buttonIdentifier, resultCallback);
                }
                else {
                    this.input.addClass(this.invalidCssClass);
                }
            }
            else {
                super.handleButtonClick(buttonIdentifier, resultCallback);
            }
        }
    }
    handleInputButtonClick(buttonIdentifier) {
        if (buttonIdentifier === "backspace") {
            this.sendBackspaceToInput();
            return true;
        }
        if (buttonIdentifier === "plus-minus") {
            this.sendPlusMinusToInput();
            return true;
        }
        if (buttonIdentifier === "decimal-point") {
            this.sendKeysToInput(this.decimalPointCharacter);
            return true;
        }
        if (NumberButtonIdentifiers.indexOf(buttonIdentifier) >= 0) {
            const digitKey = DigitButtons
                .filter(button => button.identifier === buttonIdentifier)
                .map(button => button.value)[0];
            if (typeof digitKey !== "undefined") {
                this.sendKeysToInput(`${digitKey}`);
            }
            return true;
        }
        return false;
    }
    resetInputType() {
        // restore original input type
        const value = this.getInputValue(); // keep input value
        // keeping the input value "manually" is not always necessary,
        // but sometimes the input value gets lost because of an invalid
        // decimal point format; this.setInputValue can handle this
        this.input.attr("type", this._originalInputType);
        this.setInputValue(value);
    }
    handleInputFocus(focusEvent) {
        // Why do we need this method? Explanation: Unfortunately, a number
        // input will not accept trailing decimal points. Thus we need to
        // change the input type to "text" whenever the input value results
        // in a trailing decimal point after changing it programmatically.
        // To make sure the user interacts with a standard number input,
        // we change it back to type="number" (or this._originalInputType)
        // on input focus (after removing the trailing decimal point):
        const value = this.getInputValue();
        //const decimalPointIndex: number = value.lastIndexOf(this.decimalPointCharacter);
        const decimalPointIndex = DecimalPoint.lastDecimalPointIndex(value, this.decimalPointCharacters);
        if (decimalPointIndex >= 0) {
            // found
            if (decimalPointIndex === value.length - 1) {
                // at end of string => strip it to ensure a valid number
                this.sendBackspaceToInput();
            }
        }
        this.resetInputType();
    }
    handleInputKeyEvent(keyEvent) {
        this.setDirty(true);
    }
    isValidNumber(n) {
        // this method currently only checks the decimal point
        if (DecimalPoint.hasMoreThanOneDecimalPoint(n, this.decimalPointCharacters)) {
            // more than one decimal point => invalid
            return false;
        }
        return true;
    }
    sendKeysToInput(keys) {
        const input = this.input;
        input.attr("type", "text"); // make sure the input is a text input
        // otherwise it will not accept trailing decimal points
        const originalValue = this.getInputValue();
        const isZero = ZeroPattern.test(originalValue);
        const isDirty = this.isDirty();
        const notDirtyOrZero = (!isDirty) || isZero;
        let newValue;
        if (this._numberDialogConfig.isCodeLock) {
            newValue = `${originalValue}${keys}`;
        }
        else {
            newValue = notDirtyOrZero ? keys : `${originalValue}${keys}`;
        }
        if (this.isValidNumber(newValue)) {
            this.setInputValue(newValue, false);
        }
    }
    sendPlusMinusToInput() {
        const input = this.input;
        input.attr("type", "text"); // make sure the input is a text input
        // otherwise it will not accept trailing decimal points
        const originalValue = this.getInputValue();
        // remove or prepend minus sign
        const newValue = (originalValue.indexOf("-") === 0) ?
            originalValue.substr(1) :
            "-" + originalValue;
        if (this.isValidNumber(newValue)) {
            this.setInputValue(newValue, false);
        }
    }
    sendBackspaceToInput() {
        const input = this.input;
        input.attr("type", "text"); // make sure the input is a text input
        // otherwise it will not accept trailing decimal points
        const originalValue = this.getInputValue();
        const newValue = originalValue.substr(0, originalValue.length - 1);
        if (this.isValidNumber(newValue)) {
            this.setInputValue(newValue, false);
        }
    }
    hide() {
        this.resetInputType(); // restore original input type
        super.hide();
    }
    toString() {
        return "Number Dialog";
    }
    setContentVisibility(visible) {
        super.setContentVisibility(visible);
        if (visible && this.digitButtonsDisplayed) {
            this.jQuery(this.digitButtonSelector).show();
            this.jQuery(this.inputSelector).addClass(this.inputNextToButtonsCssClass);
        }
        else {
            this.jQuery(this.digitButtonSelector).hide();
            this.jQuery(this.inputSelector).removeClass(this.inputNextToButtonsCssClass);
        }
        this.jQuery(this.decimalPointButtonSelector).text(this.decimalPointCharacter);
        if (this._numberDialogConfig.integerOnly) {
            // disable unneeded buttons
            this.jQuery(this.decimalPointButtonSelector).prop("disabled", visible);
            this.input.attr("step", "1"); // "step" is the precision attribute: "1" = integer steps
        }
        else {
            this.input.attr("step", "any"); // "step" is the precision attribute: "any" = arbitrary precision
        }
        if (this._numberDialogConfig.unsignedOnly) {
            this.jQuery(this.plusMinusButtonSelector).prop("disabled", visible);
        }
        if (!visible) {
            this.setDirty(false);
            this.input.removeAttr("step");
            this.input.blur(); // make the touch screen keyboard disappear
        }
    }
    changeInputEventSubscription(subscribe, callSuper = true) {
        if (callSuper) {
            super.changeInputEventSubscription(subscribe);
        }
        const method = subscribe ? "on" : "off";
        if (subscribe) {
            // ensure we do not subscribe twice
            this.changeInputEventSubscription(false, false); // unsubscribe
        }
        this.input[method]("focus", this._focusCallback);
        this.input[method]("keydown", this._keyCallback);
        this.input[method]("keypress", this._keyCallback);
        this.input[method]("keyup", this._keyCallback);
        console.debug("changed input focus event subscription", subscribe);
    }
}
exports.default = NumberDialog;
